import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import "@/App.css";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { Toaster } from "@/components/ui/sonner";

import LoginPage from "@/pages/LoginPage";
import AppLayout from "@/layout/AppLayout";
import Dashboard from "@/pages/Dashboard";
import PatientPortal from "@/pages/PatientPortal";
import PublicDoctorPage from "@/pages/PublicDoctorPage";
import TenantsPage from "@/pages/TenantsPage";
import MySitePage from "@/pages/MySitePage";
import InquiriesPage from "@/pages/InquiriesPage";
import PatientsPage from "@/pages/PatientsPage";
import PatientProfilePage from "@/pages/PatientProfilePage";
import AppointmentsPage from "@/pages/AppointmentsPage";
import CalendarPage from "@/pages/CalendarPage";
import EMRPage from "@/pages/EMRPage";
import PrescriptionsPage from "@/pages/PrescriptionsPage";
import LaboratoryPage from "@/pages/LaboratoryPage";
import RadiologyPage from "@/pages/RadiologyPage";
import PharmacyPage from "@/pages/PharmacyPage";
import BillingPage from "@/pages/BillingPage";
import AIPage from "@/pages/AIPage";
import HRPage from "@/pages/HRPage";
import ReportsPage from "@/pages/ReportsPage";
import ActivityPage from "@/pages/ActivityPage";
import SettingsPage from "@/pages/SettingsPage";
import AdminGatePage from "@/pages/AdminGatePage";
import SuperAdminPage from "@/pages/SuperAdminPage";

function Protected({ children }) {
  const { user } = useAuth();
  if (user === null) {
    return (
      <div className="min-h-screen grid place-items-center bg-background">
        <div className="text-sm text-muted-foreground">Loading…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user } = useAuth();
  if (user === null) return null;
  if (user) return <Navigate to="/app" replace />;
  return children;
}

function DashboardRouter() {
  const { user } = useAuth();
  if (user?.role === "super_admin") return <SuperAdminPage />;
  if (user?.role === "patient") return <PatientPortal />;
  return <Dashboard />;
}

export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Navigate to="/app" replace />} />
            <Route path="/d/:slug" element={<PublicDoctorPage />} />
            <Route path="/admin" element={<AdminGatePage />} />
            <Route path="/login" element={<PublicOnly><LoginPage /></PublicOnly>} />
            <Route path="/app" element={<Protected><AppLayout /></Protected>}>
              <Route index element={<DashboardRouter />} />
              <Route path="admin" element={<SuperAdminPage />} />
              <Route path="patients" element={<PatientsPage />} />
              <Route path="patients/:id" element={<PatientProfilePage />} />
              <Route path="appointments" element={<AppointmentsPage />} />
              <Route path="calendar" element={<CalendarPage />} />
              <Route path="emr" element={<EMRPage />} />
              <Route path="prescriptions" element={<PrescriptionsPage />} />
              <Route path="laboratory" element={<LaboratoryPage />} />
              <Route path="radiology" element={<RadiologyPage />} />
              <Route path="pharmacy" element={<PharmacyPage />} />
              <Route path="billing" element={<BillingPage />} />
              <Route path="ai" element={<AIPage />} />
              <Route path="hr" element={<HRPage />} />
              <Route path="reports" element={<ReportsPage />} />
              <Route path="activity" element={<ActivityPage />} />
              <Route path="settings" element={<SettingsPage />} />
              <Route path="tenants" element={<TenantsPage />} />
              <Route path="site" element={<MySitePage />} />
              <Route path="inquiries" element={<InquiriesPage />} />
            </Route>
            <Route path="*" element={<Navigate to="/app" replace />} />
          </Routes>
          <Toaster richColors position="top-right" />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}
